// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: putting a cited passage (ComposerQuoteNode) in the prompt box at the caret, on lines of its
// own, with the caret on the line under it; and keeping each quote on lines of its own as the text
// around it is edited. Backspace at the start of the line under a quote takes the quote out.

import {
	$getComposerSelectionRange,
	$getTextUpToCursor,
	$selectComposerNodeBoundary,
	$selectComposerRange,
} from '@app/features/lexical/composer/composerOffsets';
import {
	$createComposerQuoteNode,
	$isComposerQuoteNode,
	$removeComposerQuote,
	ComposerQuoteNode,
} from '@app/features/lexical/composer/nodes/ComposerQuoteNode';
import {mergeRegister} from '@lexical/utils';
import {
	$createLineBreakNode,
	$getRoot,
	$getSelection,
	$isLineBreakNode,
	$isRangeSelection,
	$isTextNode,
	COMMAND_PRIORITY_LOW,
	KEY_BACKSPACE_COMMAND,
	type LexicalEditor,
	type LexicalNode,
} from 'lexical';

/**
 * Puts `text` in as a quote where the caret is (`at`, or the end when the box has not had one), on the
 * line under any words before it, with the caret on the line under it.
 */
export function insertComposerQuote(editor: LexicalEditor, text: string, at: number | null): void {
	editor.update(
		() => {
			if (at === null) $getRoot().selectEnd();
			else $selectComposerRange(at, at);
			const selection = $getSelection();
			if (!$isRangeSelection(selection)) return;
			const before = $getTextUpToCursor();
			const nodes: Array<LexicalNode> = [];
			// A quote may follow words on the next line: in Markdown it starts a quote of its own.
			const lead = !before.trim() || before.endsWith('\n') ? 0 : 1;
			for (let count = 0; count < lead; count++) nodes.push($createLineBreakNode());
			const quote = $createComposerQuoteNode(text);
			const under = $createLineBreakNode();
			nodes.push(quote, under);
			selection.insertNodes(nodes);
			$selectComposerNodeBoundary(under, 'after');
		},
		{discrete: true},
	);
}

/** The node just before a collapsed caret, whether the caret sits in a text node or between nodes. */
function $nodeBeforeCaret(): LexicalNode | null {
	const selection = $getSelection();
	if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
	const {anchor} = selection;
	const node = anchor.getNode();
	if (anchor.type === 'text') {
		return anchor.offset === 0 && $isTextNode(node) ? node.getPreviousSibling() : null;
	}
	return anchor.offset > 0 && 'getChildAtIndex' in node
		? ((node as {getChildAtIndex: (index: number) => LexicalNode | null}).getChildAtIndex(anchor.offset - 1) ?? null)
		: null;
}

export function registerComposerQuotes(editor: LexicalEditor): () => void {
	return mergeRegister(
		// A quote keeps lines of its own: a line break after it (and before it, after words).
		editor.registerNodeTransform(ComposerQuoteNode, (node) => {
			if (!$isLineBreakNode(node.getNextSibling())) node.insertAfter($createLineBreakNode());
			const previous = node.getPreviousSibling();
			if (previous && !$isLineBreakNode(previous)) node.insertBefore($createLineBreakNode());
		}),
		editor.registerCommand(
			KEY_BACKSPACE_COMMAND,
			(event) => {
				const lineBreak = $nodeBeforeCaret();
				const quote = $isLineBreakNode(lineBreak) ? lineBreak.getPreviousSibling() : null;
				if (!$isComposerQuoteNode(quote)) return false;
				event?.preventDefault();
				const range = $getComposerSelectionRange();
				const length = quote.getTextContent().length + 1;
				$removeComposerQuote(quote);
				if (range) $selectComposerRange(range.start - length, range.start - length);
				return true;
			},
			COMMAND_PRIORITY_LOW,
		),
	);
}
